import { environment } from "@app/relay/relay-environment";
import {
  ExportList,
  isPending,
  usePollWhilePending,
} from "@app/pages/exports/exports-page";
import { Link } from "@tanstack/react-router";
import { Suspense, useEffect, useRef, useState } from "react";
import {
  commitMutation,
  fetchQuery,
  graphql,
  usePreloadedQuery,
} from "react-relay";
import type { PreloadedQuery } from "react-relay";

import type { diligenceReportPage_Query } from "@app/queries/diligenceReportPage_Query.graphql";
import type { diligenceReportPageGenerateMutation } from "@app/queries/diligenceReportPageGenerateMutation.graphql";
import DiligenceKpiCellPanel, {
  type DiligenceKpiCellScope,
} from "./diligence-kpi-cell-panel";
import DiligenceMatrixTables from "./diligence-matrix-tables";

export const diligenceReportQuery = graphql`
  query diligenceReportPage_Query {
    exports(kind: "DILIGENCE_KPIS") {
      id
      kind
      kindLabel
      label
      status
      summary
      byteSize
      errorMessage
      createdAt
      finishedAt
      filename
      downloadPath
    }
  }
`;

const generateMutation = graphql`
  mutation diligenceReportPageGenerateMutation {
    exportCreate(kind: "DILIGENCE_KPIS") {
      export {
        id
        status
      }
      errors {
        message
      }
    }
  }
`;

// What the workbook contains, for a reader deciding whether to wait for one.
// The same numbers are on screen below; this is the file's own table of
// contents, including the parts the segment picker hides one at a time.
const CONTENTS = [
  "NRR, GRR and both logo-churn measures — the numbers behind the retention charts",
  "MRR movements: new, expansion, contraction, churned, reactivation, net new",
  "Ending MRR and ARR",
  "Customers active, added, lost and reactivated",
  "ARPU, quick ratio, gross and net revenue churn",
  "Lifetime value six ways — three ARPU ÷ churn projections, implied lifetime in months, the historic average of completed lifetimes, and realised revenue per active customer — plus LTV : CAC",
  "Revenue mix — recurring, SMS and one-time, in dollars and as a share",
  "Billed volume and trials started",
];

export default function DiligenceReportPage({
  queryRef,
}: {
  queryRef: PreloadedQuery<diligenceReportPage_Query>;
}) {
  const preloaded = usePreloadedQuery(diligenceReportQuery, queryRef);
  const [data, setData] = useState(preloaded);
  const [starting, setStarting] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [cell, setCell] = useState<DiligenceKpiCellScope | null>(null);
  // Bumped when a run finishes, so the tables pick up the matrix it just stored.
  const [matrixKey, setMatrixKey] = useState(0);

  // Re-sync only when the route hands a NEW query, keyed on queryRef — refresh()
  // writes the same root into the store, so keying on `preloaded` would re-emit
  // the stale snapshot and clobber what the poll just fetched.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setData(preloaded), [queryRef]);

  const refresh = () =>
    fetchQuery<diligenceReportPage_Query>(
      environment,
      diligenceReportQuery,
      {},
      { fetchPolicy: "network-only" }
    )
      .toPromise()
      .then((fresh) => {
        if (fresh) setData(fresh);
      });

  const rows = data.exports;
  const pending = isPending(rows);
  usePollWhilePending(pending, refresh);

  // A run that has just stopped being pending has stored a new matrix. The
  // tables are read from that row, so they are stale until they refetch.
  const wasPending = useRef(pending);
  useEffect(() => {
    if (wasPending.current && !pending) setMatrixKey((key) => key + 1);
    wasPending.current = pending;
  }, [pending]);

  const generate = () => {
    setStarting(true);
    setNotice(null);
    commitMutation<diligenceReportPageGenerateMutation>(environment, {
      mutation: generateMutation,
      variables: {},
      onCompleted: (response, errors) => {
        setStarting(false);
        const failure = (errors ?? response.exportCreate?.errors)?.[0]?.message;
        setNotice(
          failure ??
            "Generating — the tables and the download appear here when the workbook is ready."
        );
        refresh();
      },
      onError: () => {
        setStarting(false);
        setNotice("Could not start the export. Please try again.");
      },
    });
  };

  return (
    <div className="mx-auto max-w-full space-y-6 p-6">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-xl font-bold">Diligence KPI report</h1>
        <Link
          to="/diligence"
          className="ml-auto rounded border border-gray-200 px-2 py-1 text-sm text-gray-600 hover:bg-gray-50"
        >
          ← Back to diligence
        </Link>
        <Link
          to="/exports"
          className="rounded border border-gray-200 px-2 py-1 text-sm text-gray-600 hover:bg-gray-50"
        >
          All exports
        </Link>
      </div>

      <div className="max-w-3xl space-y-2 text-sm text-gray-500">
        <p>
          Every metric as a row, every month of company history as a column, on
          two bases — trailing twelve months and month over month — for the
          whole book or any seat or tenure band.
        </p>
        <p>
          <span className="font-medium text-gray-700">Click any number</span> to
          see what it is made of: the customers in the cohort and what each was
          paying then and now, the customer-months behind a movement, the
          invoices behind a revenue line. The panel restates the arithmetic and
          adds the rows up for you, so the figure can be checked rather than
          taken on trust.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={generate}
          disabled={starting || pending}
          className="rounded bg-gray-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-700 disabled:opacity-40"
        >
          {pending ? "Generating…" : starting ? "Starting…" : "Generate report"}
        </button>
        {notice && <span className="text-xs text-gray-500">{notice}</span>}
        <span className="text-xs text-gray-400">
          The matrix self-joins the customer-month fact table across company
          history, so it is built in the background and stored. The tables below
          are that stored report — the same numbers as the workbook, to the
          cent.
        </span>
      </div>

      <Suspense
        fallback={
          <div className="p-6 text-sm text-gray-400">Loading the report…</div>
        }
      >
        <DiligenceMatrixTables refreshKey={matrixKey} onCell={setCell} />
      </Suspense>

      <details className="max-w-3xl text-sm text-gray-500">
        <summary className="cursor-pointer text-gray-600">
          What the workbook contains
        </summary>
        <ul className="mt-2 list-disc space-y-1 pl-5">
          {CONTENTS.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </details>

      <ExportList
        rows={rows}
        emptyHint="No report generated yet. Press Generate report to build one."
      />

      {cell && (
        <DiligenceKpiCellPanel
          scope={cell}
          refreshToken={matrixKey}
          onClose={() => setCell(null)}
        />
      )}
    </div>
  );
}
